import type { PresentationInput } from '../presentation-input';
import { preparePresentation, type PreparedPresentation } from './generate-pdf';
import type { PdfImage } from './pdf-kit';
import type { Picture, SvgAssets, SvgInput } from './render-svg';
import { dataUri } from './svg-kit';

/**
 * Выгрузка презентации в SVG — файл, который открывают в Figma.
 *
 * Картинки и шрифты готовит тот же шаг, что и для PDF: два формата обязаны
 * показывать одно и то же. Разница только в том, что сюда картинка уезжает
 * строкой data: — иначе Figma получила бы файл со ссылками на соседей.
 */

function toPicture(image: PdfImage): Picture {
  return { href: dataUri(image.data, image.format), width: image.width, height: image.height };
}

const optional = (image: PdfImage | null): Picture | null => (image ? toPicture(image) : null);

/** Ассеты макета → картинки для SVG. */
export function svgAssetsOf(a: PreparedPresentation['assets']): SvgAssets {
  return {
    fonts: a.fonts,
    phoneFrame: toPicture(a.phoneFrame),
    tagShadow: toPicture(a.tagShadow),
    hand: toPicture(a.hand),
    laptop: toPicture(a.laptop),
    swirl: toPicture(a.swirl),
    coverBackground: toPicture(a.coverBackground),
    iconMail: toPicture(a.iconMail),
    iconPhone: toPicture(a.iconPhone),
    iconTelegram: toPicture(a.iconTelegram),
    screens: { reel: toPicture(a.screens.reel), post: toPicture(a.screens.post) },
  };
}

/** Данные презентации → вход рендера SVG. */
export function svgInputOf(input: PresentationInput, prepared: PreparedPresentation): SvgInput {
  return {
    logo: optional(prepared.logo),
    period: input.period,
    cover: optional(prepared.cover),
    posts: input.posts.map((p, i) => ({
      badge: p.badge,
      url: p.url,
      username: p.username,
      views: p.views,
      reach: p.reach,
      likes: p.likes,
      comments: p.comments,
      saved: p.saved,
      shares: p.shares,
      reposts: p.reposts,
      totalInteractions: p.totalInteractions,
      profileVisits: p.profileVisits,
      follows: p.follows,
      avgWatchMs: p.avgWatchMs,
      totalWatchMs: p.totalWatchMs,
      skipRate: p.skipRate,
      photo: optional(prepared.posts[i]),
      thumb: optional(prepared.thumbnails[i]),
    })),
    articles: input.articles.map((article, i) => ({
      screenshot: toPicture(prepared.screenshots[i]),
      domain: article.domain,
      url: article.url,
    })),
    contact: input.contact,
  };
}

export async function generatePresentationSvg(
  input: PresentationInput,
  onProgress: (message: string) => void = () => {},
): Promise<Blob> {
  const [{ jsPDF }, prepared] = await Promise.all([
    import('jspdf'),
    preparePresentation(input, onProgress),
  ]);

  onProgress('Сборка SVG...');
  const { renderPresentationSvg } = await import('./render-svg');
  const svg = renderPresentationSvg(jsPDF, svgInputOf(input, prepared), svgAssetsOf(prepared.assets));
  return new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
}
